import {ConnectionState, RocketChatSocketClient} from "./RocketChatSocketClient";
import {RocketChatHttpClient} from "./RocketChatHttpClient";
import {ChatMessage} from "../../types/chat-message";
import {ChatConversation} from "../../types/chat-conversation";
import {chunk} from "../../../util/chunk";
import moment from "moment";

export class RocketChatService {
  private _messages: Map<string,ChatMessage> = new Map()
  private _rooms: ChatConversation[] = []
  private _roomsThatListenToDeletedMessages: ChatConversation['id'][] = []
  private _roomHistoryPaginationCursors: Map<string,number> = new Map()
  private _historyChangedHandler: ((history: ChatMessage[]) => void)|null = null
  private _roomsChangedHandler: ((history: ChatConversation[]) => void)|null = null

  constructor(
    private readonly socketClient: RocketChatSocketClient,
    private readonly httpClient: RocketChatHttpClient,
  ) {
  }

  async connect(chatToken: string) {
    if (this.socketClient.getState() !== ConnectionState.disconnected) {
      console.warn('Attempting to connect to RocketChat while already connected. Skipping...')
      return
    }
    this.socketClient.onMessageReceived(message => {
      this._messages.set(message.id, message);
      this._historyChangedHandler?.(Array.from(this._messages.values()));
    })
    this.socketClient.onMessageDeleted(messageId => {
      this._messages.delete(messageId);
      this._historyChangedHandler?.(Array.from(this._messages.values()));
    })
    await this.socketClient.connect();
    await this.socketClient.login(chatToken)
    await this.socketClient.subscribeToMessages()
    await this.updateRooms()
    // This is disabled until we are certain that it is no longer necessary.
    // await this.preloadHistory()
  }

  disconnect() {
    this.socketClient.disconnect();
  }

  async updateRooms(): Promise<void> {
    const changes = await this.socketClient.getRoomChanges()
    // First, remove any room that was updated or deleted, we will re-add the updated rooms in the next step.
    this._rooms = this._rooms.filter(room => {
      if (changes.updated.some(r => r._id === room.id)) {
        return false;
      }
      return !changes.removed.includes(room.id);
    })
    // Now, re-add the rooms that received updates
    changes.updated.forEach((room) => {
      let originalRoom = this._rooms.find((or) => or.id === room._id )
      let roomName = !!room.name ? room.name.replaceAll('_', ' ') : 'NO RCHAT NAME'
      roomName = roomName.includes('.') ? roomName.split('.')[1] : roomName
      if (room.t === 'p') {
        this._rooms.push({
          id: room._id,
          name: roomName,
          timestamp: new Date(room.ts.$date),
          lastMessage: room.lastMessage ? {
            id: room.lastMessage._id,
            roomId: room.lastMessage.rid,
            message: room.lastMessage.msg,
            attachments: room.lastMessage.attachments,
            file: room.lastMessage.file,
            timestamp: new Date(room.lastMessage.ts.$date),
          } : undefined,
          purgedAt: originalRoom ? originalRoom.purgedAt : null ,
        })
        if (room.lastMessage) {
          this._messages.set(room.lastMessage._id,{
            id: room.lastMessage._id,
            conversationId: room.lastMessage.rid,
            userId: room.lastMessage.u._id,
            message: room.lastMessage.msg,
            attachments: room.lastMessage.attachments,
            file: room.lastMessage.file,
            read: false,
            timestamp: new Date(room.lastMessage.ts.$date),
          })
        }
      }
    })
    this._roomsChangedHandler?.(this._rooms);
  }

  async preloadHistory(): Promise<void> {
    // only
    const filteredMessages = this._rooms.filter((room) => room.lastMessage === undefined || moment(room.lastMessage.timestamp).isAfter(moment().subtract(5, 'days')));
    const chunkPromises = chunk(filteredMessages, 50).map(async (chunk) => {
      const loadedMessages = (await Promise.all(
        chunk.map(async (room) => {
          const history = await this.socketClient.loadHistory(room.id, 300);
          const oldestTimeStamp = history.length > 0 ? Math.min(...history.map(m => m.timestamp.getTime())) : (new Date()).getTime()
          this._roomHistoryPaginationCursors.set(room.id, oldestTimeStamp)
          return history;
        })
      )).flat()
      loadedMessages.forEach((message) => {
        this._messages.set(message.id, message)
      })
      this._historyChangedHandler?.(Array.from(this._messages.values()));
    })
    await Promise.all(chunkPromises)
  }

  canLoadMoreHistory(conversationId: string): boolean {
    const room = this._rooms?.find(r => r.id === conversationId)
    if (!room) {
      return false;
    }
    const roomTime = room?.timestamp.getTime()
    const timeCursor = this._roomHistoryPaginationCursors.get(room.id)
    if (!timeCursor) {
      return true;
    }
    return roomTime < timeCursor
  }

  async loadMoreHistory(conversationId: string, limit: number = 300): Promise<void> {
    const cursor = this._roomHistoryPaginationCursors.get(conversationId)
    const toTime = cursor ?? (new Date()).getTime()
    const fromTime = toTime - 90 * 24 * 3600 * 1000
    this.ensureRoomIsSubscribedToDeletedMessageEvents(conversationId)
    const loadedMessages = cursor ? await this.socketClient.loadHistory(conversationId, limit, fromTime, toTime) : await this.socketClient.loadHistory(conversationId, 300)
    const oldestTimeStamp = loadedMessages.length > 0 ? Math.min(...loadedMessages.map(m => m.timestamp.getTime())) : fromTime
    this._roomHistoryPaginationCursors.set(conversationId, oldestTimeStamp)
    loadedMessages.forEach((message) => {
      this._messages.set(message.id, message)
    })
    this._historyChangedHandler?.(Array.from(this._messages.values()));
  }

  ensureRoomIsSubscribedToDeletedMessageEvents(roomId: string): void {
    if (! this._roomsThatListenToDeletedMessages.includes(roomId)) {
      this.socketClient.subscribeToDeletedMessages(roomId)
      this._roomsThatListenToDeletedMessages.push(roomId)
    }
  }

  async sendChatMessage(conversationId: string, chatMessage: string) {
    await this.socketClient.sendMessage(conversationId, chatMessage)
  }

  async sendAttachment(userId: string, token: string, conversationId: string, attachment: File) {
    await this.httpClient.uploadAttachment(userId, token, conversationId, attachment)
  }

  async deleteChatMessage(userId: string, token: string, conversationId: string, messageId: string): Promise<void> {
    this._messages.delete(messageId)
    this._historyChangedHandler?.(Array.from(this._messages.values()));
    await this.httpClient.deleteMessage(userId, token, conversationId, messageId)
  }

  formatMediaUrl(userId: string, token: string, path: string) {
    return this.httpClient.formatMediaUrl(userId, token, path)
  }

  onHistoryChanged(handler: (history: ChatMessage[]) => void) {
    this._historyChangedHandler = handler
  }

  onRoomsChanged(handler: (history: ChatConversation[]) => void) {
    this._roomsChangedHandler = handler
  }
}